/**
 * Сервис логирования и обработки ошибок для синхронизации полей
 * Обеспечивает отслеживание операций, ошибок и уведомления пользователей
 */

import { notification } from 'antd';


const LOG_CONFIG = {
    enableConsoleLog: true,
    enableNotifications: false,
    logLevel: 'INFO', 
    maxLogEntries: 100
};


let syncLogs = [];

/**
 * Запись лога синхронизации
 * @param {string} level - Уровень лога (DEBUG, INFO, WARN, ERROR)
 * @param {string} message - Сообщение
 * @param {Object} data - Дополнительные данные
 */
function log(level, message, data = {}) {
    const logEntry = {
        timestamp: new Date().toISOString(),
        level,
        message,
        data,
        id: `sync_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };
    
    
    syncLogs.unshift(logEntry);
    
    
    if (syncLogs.length > LOG_CONFIG.maxLogEntries) {
        syncLogs = syncLogs.slice(0, LOG_CONFIG.maxLogEntries);
    }
    
    
    if (LOG_CONFIG.enableConsoleLog) {
        const consoleMethod = level === 'ERROR' ? 'error' : 
                            level === 'WARN' ? 'warn' : 
                            level === 'DEBUG' ? 'debug' : 'log';
        
        console[consoleMethod](`[SYNC ${level}] ${message}`, data);
    }
}

/**
 * Логирование успешной синхронизации
 * @param {Object} customField - Кастомное поле
 * @param {*} value - Синхронизированное значение
 * @param {Object} amoField - AMO поле
 * @param {Object} result - Результат синхронизации
 */
export function logSyncSuccess(customField, value, amoField, result) {
    log('INFO', 'Синхронизация выполнена успешно', {
        customField: {
            id: customField.id,
            name: customField.name,
            type: customField.type
        },
        amoField: {
            id: amoField.id,
            name: amoField.name,
            type: amoField.type
        },
        value,
        syncedValue: result.syncedValue,
        message: result.message
    });
    
    if (LOG_CONFIG.enableNotifications) {
        notification.success({
            message: 'Синхронизация успешна',
            description: `Поле "${customField.name}" синхронизировано с AMO полем "${amoField.name}"`,
            placement: 'bottomRight',
            duration: 3
        });
    }
}

/**
 * Логирование ошибки синхронизации
 * @param {Object} customField - Кастомное поле
 * @param {*} value - Значение, которое пытались синхронизировать
 * @param {string} error - Описание ошибки
 * @param {Object} details - Дополнительные детали ошибки
 */
export function logSyncError(customField, value, error, details = {}) {
    log('ERROR', 'Ошибка синхронизации', {
        customField: {
            id: customField.id,
            name: customField.name,
            type: customField.type
        },
        value,
        error,
        details,
        stackTrace: details.stackTrace || new Error().stack,
        amoData: {
            hasCurrentCard: !!AMOCRM?.data?.current_card,
            hasAttributes: !!AMOCRM?.data?.current_card?.model?.attributes,
            fieldValue: AMOCRM?.data?.current_card?.model?.attributes?.[`CFV[${details.amoFieldId || 'unknown'}]`]
        }
    });
    
    if (LOG_CONFIG.enableNotifications) {
        notification.error({
            message: 'Ошибка синхронизации',
            description: `Не удалось синхронизировать поле "${customField.name}": ${error}`,
            placement: 'bottomRight',
            duration: 5
        });
    }
}

/**
 * Логирование предупреждения синхронизации
 * @param {Object} customField - Кастомное поле
 * @param {*} value - Значение
 * @param {string} warning - Предупреждение
 * @param {Object} details - Дополнительные детали
 */
export function logSyncWarning(customField, value, warning, details = {}) {
    log('WARN', 'Предупреждение синхронизации', {
        customField: {
            id: customField.id,
            name: customField.name,
            type: customField.type
        },
        value,
        warning,
        details
    });
    
    if (LOG_CONFIG.enableNotifications) {
        notification.warning({
            message: 'Предупреждение синхронизации',
            description: `Поле "${customField.name}": ${warning}`,
            placement: 'bottomRight',
            duration: 4
        });
    }
}

/**
 * Логирование конфликта синхронизации
 * @param {Object} customField - Кастомное поле
 * @param {*} customValue - Значение кастомного поля
 * @param {Object} conflict - Информация о конфликте
 */
export function logSyncConflict(customField, customValue, conflict) {
    log('WARN', 'Конфликт синхронизации', {
        customField: {
            id: customField.id,
            name: customField.name,
            type: customField.type
        },
        customValue,
        conflict
    });
    
    if (LOG_CONFIG.enableNotifications) {
        notification.warning({
            message: 'Конфликт синхронизации',
            description: `Поле "${customField.name}" конфликтует с AMO полем "${conflict.amoFieldName}". AMO поле уже содержит другое значение.`,
            placement: 'bottomRight',
            duration: 6
        });
    }
}

/**
 * Логирование информации о сопоставлении
 * @param {Object} customField - Кастомное поле
 * @param {Object} amoField - AMO поле
 * @param {string} matchType - Тип сопоставления (exact, partial, contains)
 */
export function logFieldMapping(customField, amoField, matchType = 'exact') {
    log('DEBUG', 'Сопоставление полей', {
        customField: {
            id: customField.id,
            name: customField.name,
            type: customField.type
        },
        amoField: {
            id: amoField.id,
            name: amoField.name,
            type: amoField.type
        },
        matchType
    });
}

/**
 * Обработка исключений синхронизации
 * @param {Error} error - Объект ошибки
 * @param {Object} context - Контекст операции
 * @returns {Object} Стандартизированный ответ об ошибке
 */
export function handleSyncException(error, context = {}) {
    const errorDetails = {
        message: error.message,
        stack: error.stack,
        context,
        timestamp: new Date().toISOString()
    };
    
    log('ERROR', 'Исключение при синхронизации', errorDetails);
    
    
    let userMessage = 'Произошла ошибка синхронизации';
    
    if (error.name === 'TypeError') {
        userMessage = 'Ошибка типа данных при синхронизации';
    } else if (error.name === 'ReferenceError') {
        userMessage = 'Ошибка доступа к данным AMO';
    } else if (error.message.includes('network')) {
        userMessage = 'Ошибка сети при синхронизации';
    } else if (error.message.includes('permission')) {
        userMessage = 'Недостаточно прав для синхронизации';
    }
    
    if (LOG_CONFIG.enableNotifications) {
        notification.error({
            message: 'Ошибка системы',
            description: userMessage,
            placement: 'bottomRight',
            duration: 5
        });
    }
    
    return {
        success: false,
        error: userMessage,
        details: errorDetails
    };
}

/**
 * Получение последних логов синхронизации
 * @param {number} count - Количество записей
 * @param {string} level - Фильтр по уровню (опционально)
 * @returns {Array} Массив записей логов
 */
export function getSyncLogs(count = 20, level = null) {
    let filteredLogs = syncLogs;
    
    if (level) {
        filteredLogs = syncLogs.filter(log => log.level === level);
    }
    
    return filteredLogs.slice(0, count);
}

/**
 * Очистка логов синхронизации
 */
export function clearSyncLogs() {
    syncLogs = [];
    log('INFO', 'Логи синхронизации очищены');
}

/**
 * Получение статистики синхронизации
 * @param {number} hoursBack - За сколько часов назад собирать статистику
 * @returns {Object} Статистика операций
 */
export function getSyncStatistics(hoursBack = 24) {
    const cutoffTime = new Date(Date.now() - hoursBack * 60 * 60 * 1000);
    
    const recentLogs = syncLogs.filter(log => 
        new Date(log.timestamp) > cutoffTime
    );
    
    const stats = {
        total: recentLogs.length,
        success: recentLogs.filter(log => 
            log.level === 'INFO' && log.message.includes('успешно')
        ).length,
        errors: recentLogs.filter(log => log.level === 'ERROR').length,
        warnings: recentLogs.filter(log => log.level === 'WARN').length,
        timeRange: `${hoursBack} часов`
    };
    
    stats.successRate = stats.total > 0 ? 
        Math.round((stats.success / stats.total) * 100) : 0;
    
    return stats;
}

/**
 * Экспорт логов в JSON
 * @returns {string} JSON строка с логами
 */
export function exportSyncLogs() {
    try {
        return JSON.stringify({
            exportTime: new Date().toISOString(),
            logs: syncLogs,
            statistics: getSyncStatistics()
        }, null, 2);
    } catch (error) {
        console.error('Ошибка экспорта логов:', error);
        return '{"error": "Не удалось экспортировать логи"}';
    }
}

/**
 * Настройка конфигурации логирования
 * @param {Object} config - Новая конфигурация
 */
export function configureSyncLogging(config) {
    Object.assign(LOG_CONFIG, config);
    log('INFO', 'Конфигурация логирования обновлена', config);
} 